import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DarkBackground from '../components/DarkBackground';
import { verifyTurnstileToken } from '../lib/turnstileVerification';
import { useAuth } from '../contexts/AuthContext';

// Definir tipo para el objeto global de Cloudflare Turnstile
declare global {
  interface Window {
    turnstile: any;
    onTurnstileLoad: () => void;
  }
}

// Clave del sitio de Cloudflare Turnstile
// Usando una clave de prueba válida para desarrollo
// En el error 400020, la clave anterior no era válida o tenía restricciones de dominio
const TURNSTILE_SITE_KEY = "1x00000000000000000000AA";

const CaptchaVerification: React.FC = () => {
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const captchaRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const navigate = useNavigate();
  const isComponentMounted = useRef(true);
  const { user } = useAuth(); // Obtener el estado de autenticación

  // Efecto para verificar si el usuario ya está autenticado
  useEffect(() => {
    // Si el usuario ya está autenticado, redirigir a la página principal 
    // sin necesidad de la verificación CAPTCHA
    if (user) {
      navigate('/home');
    }
  }, [user, navigate]);

  // Controlar el ciclo de vida del componente
  useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
      cleanupTurnstile();
    };
  }, []);

  // Función para limpiar recursos del CAPTCHA
  const cleanupTurnstile = () => {
    try {
      // Eliminar scripts de Turnstile
      const turnstileScripts = document.querySelectorAll('script[src*="turnstile"]');
      turnstileScripts.forEach(script => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      });
      
      // Limpiar callbacks globales
      if (window.onTurnstileLoad) {
        window.onTurnstileLoad = () => {};
      }
      
      // Limpiar el widget si existe
      if (window.turnstile && widgetId.current) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    } catch (error) {
      console.error('Error al limpiar Turnstile:', error);
    }
  };

  // Cargar el script de Cloudflare Turnstile
  useEffect(() => {
    // No cargar el script si el usuario ya está autenticado
    if (user) return;

    if (!scriptLoaded) {
      // Si ya está cargado, renderizar directamente
      if (window.turnstile && typeof window.turnstile.render === 'function') {
        renderTurnstile();
        return;
      }

      // Función de callback cuando se carga el script
      window.onTurnstileLoad = () => {
        if (isComponentMounted.current) {
          setTimeout(() => {
            renderTurnstile();
          }, 100);
        }
      };

      try {
        // Eliminar cualquier script existente para evitar duplicaciones
        const existingScript = document.querySelector('script[src*="turnstile"]');
        if (existingScript) {
          document.head.removeChild(existingScript);
        }
        
        // Cargar el script de Turnstile
        // Incluir hl=es para idioma español y retry=false para evitar reintentos automáticos
        const script = document.createElement('script');
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit&hl=es&retry=false";
        script.async = true;
        script.defer = true;
        script.onerror = () => {
          console.error('Error al cargar el script de Turnstile');
          if (isComponentMounted.current) {
            setError('Error al cargar el captcha. Por favor, recarga la página.');
          }
        };
        document.head.appendChild(script);
      } catch (error) {
        console.error('Error al cargar script de Turnstile:', error);
        setError('Error al cargar el captcha. Por favor, recarga la página.');
      }
    }
  }, [scriptLoaded, user]);

  // Renderizar el widget de Turnstile
  const renderTurnstile = () => {
    if (!isComponentMounted.current) return;
    
    try {
      if (captchaRef.current && window.turnstile && typeof window.turnstile.render === 'function') {
        // Remover widget existente si hay uno
        if (widgetId.current) {
          try {
            window.turnstile.remove(widgetId.current);
          } catch (e) {
            console.error('Error al eliminar widget existente:', e);
          }
          widgetId.current = null;
        }
        
        // Renderizar nuevo widget con opciones explícitas
        widgetId.current = window.turnstile.render(captchaRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            if (isComponentMounted.current) {
              console.log('Token generado correctamente');
              setToken(token);
            }
          },
          'expired-callback': () => {
            if (isComponentMounted.current) {
              console.log('Token expirado');
              setToken(null);
            }
          },
          'error-callback': (error: any) => {
            if (isComponentMounted.current) {
              console.error('Error de Turnstile:', error);
              setToken(null);
              setError('Error en la verificación del captcha. Por favor, inténtalo de nuevo.');
            }
          },
          theme: 'dark',
          language: 'es',
          size: 'normal',
          appearance: 'interaction-only'
        });
        
        setScriptLoaded(true);
      }
    } catch (error) {
      console.error('Error al renderizar Turnstile:', error);
      setError('Error al inicializar el captcha. Por favor, recarga la página.');
    }
  };

  // Effect para verificar automáticamente cuando se obtiene un token
  useEffect(() => {
    if (token && !loading && !verificationSuccess) {
      handleVerification();
    }
  }, [token]);

  // Manejar la verificación y redirección
  const handleVerification = async () => {
    if (!token) {
      setError('Por favor, completa el captcha para continuar.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Verificar el token con nuestra función de utilidad
      const result = await verifyTurnstileToken(token);
      
      if (isComponentMounted.current) {
        if (result.success) {
          setVerificationSuccess(true);
          // Redireccionar después de un breve retraso para mostrar el mensaje de éxito
          setTimeout(() => {
            if (isComponentMounted.current) {
              // Redirigir a /home si el usuario ya está autenticado, o a /login si no
              navigate(user ? '/home' : '/login');
            }
          }, 1000);
        } else {
          throw new Error(result.message || 'Verificación fallida');
        }
      }
    } catch (err: any) {
      console.error('Error en verificación:', err);
      if (isComponentMounted.current) {
        setError(err.message || 'Error al verificar el captcha. Por favor, inténtalo de nuevo.');
        setLoading(false);
        
        // Resetear el captcha
        if (window.turnstile && widgetId.current) {
          try {
            window.turnstile.reset(widgetId.current);
          } catch (e) {
            console.error('Error al resetear el widget:', e);
            // Si hay error al resetear, intentar volver a renderizar
            setScriptLoaded(false);
            setTimeout(renderTurnstile, 500);
          }
          setToken(null);
        }
      }
    }
  };

  // Función para manejar errores de carga
  const handleRetry = () => {
    if (isComponentMounted.current) {
      setScriptLoaded(false);
      setError(null);
      cleanupTurnstile();
      setTimeout(() => {
        if (isComponentMounted.current) {
          renderTurnstile();
        }
      }, 300);
    }
  };

  // Si el usuario ya está autenticado, no mostrar nada (se redirigirá automáticamente)
  if (user) {
    return null;
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      <DarkBackground />
      <div className="absolute inset-0 z-10 flex items-center justify-center">
        <div className="max-w-md w-full p-6">
          <div className="bg-gray-900/80 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-800/50 overflow-hidden p-8">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-white mb-2 flex justify-center items-center">
                To<span className="text-blue-400">Watch</span>
                <div className="w-10 h-10 ml-3 bg-blue-900/20 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
              </h1>
              <p className="text-gray-300 mt-4">
                {verificationSuccess 
                  ? '¡Verificación exitosa! Redirigiendo...' 
                  : 'Por favor, verifica que eres humano para continuar'}
              </p>
            </div>

            {error && (
              <div className="bg-red-900/20 border border-red-800/30 text-red-300 px-4 py-3 rounded-lg mb-6 flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{error}</span>
                </div>
                <button 
                  onClick={handleRetry}
                  className="text-red-300 hover:text-white transition-colors ml-2"
                >
                  Reintentar
                </button>
              </div>
            )}

            {verificationSuccess && (
              <div className="bg-green-900/20 border border-green-800/30 text-green-300 px-4 py-3 rounded-lg mb-6 flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Verificación exitosa. Serás redirigido en breve...</span>
              </div>
            )}

            <div className="flex flex-col items-center justify-center space-y-6">
              {!verificationSuccess && (
                <div ref={captchaRef} className="flex justify-center"></div>
              )}
              
              {loading && (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin h-8 w-8 text-blue-400" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaptchaVerification; 